import {Performanceable} from "../../models/accounting/Performanceable";
import {ProjectStagePhaseIdPath, ProjectStagePhaseIdPathProp} from "../../models/projects/Project";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {Percent} from "../../models/accounting/Finance";
import {asyncScheduler, concat, Observable, of} from "rxjs";
import {ofType, StateObservable} from "redux-observable";
import {AppState} from "../../reducers/RootReducer";
import {catchError, map, mergeMap, switchMap} from "rxjs/operators";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {ajax} from "rxjs/ajax";
import {RouteMethods} from "../../config/RouteConfig";
import {eventNotificationError, eventNotificationSuccess} from "../common/EventNotificationActions";
import {Nullable} from "../../models/common/Nullable";
import {ProjectStagePhaseTaskId, ProjectStagePhaseTaskModule} from "../../models/projects/ProjectStagePhaseTask";
import {Identifier} from "../../models/common/Identifier";
import {fetchProjectById} from "./ProjectActions";
import {fetchProjectAppendix} from "./ProjectAppendixActions";
import {getInitialViewSettings} from "../../models/common/ViewSettings";
import {ProjectAppendixModule} from "../../models/projects/ProjectAppendixModule";
import {ProjectPerformanceChangeType} from "../../models/projects/ProjectPerformanceChangeType";

/** backend api path */
function apiPath(
    phaseIdPath: ProjectStagePhaseIdPath,
    id: Nullable< number > = null
): string {
    if( id === null ) {
        return `${ APP_CONFIG.baseWeb }projects/${ phaseIdPath.projectId.rawValue }/stages/${ phaseIdPath.projectStageId.rawValue }/phases/${phaseIdPath.phaseId.rawValue}/tasks`;
    } else {
        return `${ APP_CONFIG.baseWeb }projects/${ phaseIdPath.projectId.rawValue }/stages/${ phaseIdPath.projectStageId.rawValue }/phases/${phaseIdPath.phaseId.rawValue}/tasks/${id}`;
    }
}

/** union of all Project related action types */
export type ProjectProjectStagePhaseTaskViewActionTypes = UpdatePhaseTaskPerformanceActionTypes
    | FetchProjectPhaseTaskNextIdActionTypes

/**
 * UPDATE phase performance
 */
export interface UpdatePhaseTaskPerformanceAction extends ProjectStagePhaseTaskId, Performanceable, ProjectStagePhaseIdPathProp, ReduxAction< typeof UPDATE_PHASE_TASK_PERFORMANCE> {}
export interface UpdatePhaseTaskPerformanceSuccessAction extends Performanceable, ProjectStagePhaseIdPathProp, ReduxAction<typeof UPDATE_PHASE_TASK_PERFORMANCE_SUCCESS> {}
export interface UpdatePhaseTaskPerformanceErrorAction  extends ProjectStagePhaseIdPathProp, ErrorAction<typeof UPDATE_PHASE_TASK_PERFORMANCE_ERROR> {}

export type UpdatePhaseTaskPerformanceActionTypes = UpdatePhaseTaskPerformanceAction | UpdatePhaseTaskPerformanceSuccessAction | UpdatePhaseTaskPerformanceErrorAction

export const UPDATE_PHASE_TASK_PERFORMANCE = "UPDATE_PHASE_TASK_PERFORMANCE";
export const UPDATE_PHASE_TASK_PERFORMANCE_SUCCESS = "UPDATE_PHASE_TASK_PERFORMANCE_SUCCESS";
export const UPDATE_PHASE_TASK_PERFORMANCE_ERROR = "UPDATE_PHASE_TASK_PERFORMANCE_ERROR";

export function updatePhaseTaskPerformance(
    phaseIdPath: ProjectStagePhaseIdPath,
    projectStagePhaseTaskId: Identifier< number >,
    performance: Percent
): UpdatePhaseTaskPerformanceAction {
    return { phaseIdPath, type: UPDATE_PHASE_TASK_PERFORMANCE, performance, projectStagePhaseTaskId };
}

export function updatePhaseTaskPerformanceSuccess(
    phaseIdPath: ProjectStagePhaseIdPath,
    performance: Percent
): UpdatePhaseTaskPerformanceSuccessAction {
    return { type: UPDATE_PHASE_TASK_PERFORMANCE_SUCCESS, phaseIdPath, performance };
}

export function updatePhaseTaskPerformanceError(
    phaseIdPath: ProjectStagePhaseIdPath,
    error: any
): UpdatePhaseTaskPerformanceErrorAction {
    return { type: UPDATE_PHASE_TASK_PERFORMANCE_ERROR, phaseIdPath, error };
}

export function updatePhaseTaskPerformanceEpic(
    action$: Observable< UpdatePhaseTaskPerformanceAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( UPDATE_PHASE_TASK_PERFORMANCE ),
        switchMap( action => {

            const url = apiPath( action.phaseIdPath, action.projectStagePhaseTaskId.rawValue ) + "/" + action.performance;

            return ajax( APP_CONFIG.getRequestObject( url, RouteMethods.patch ) ).pipe(
                mergeMap( _ => of(
                    updatePhaseTaskPerformanceSuccess( action.phaseIdPath, action.performance ),
                    fetchProjectById( action.phaseIdPath.projectId ),
                    fetchProjectAppendix( action.phaseIdPath.projectId, getInitialViewSettings( ProjectAppendixModule.identifier ) ),
                    eventNotificationSuccess()
                )),
                catchError( error => of(
                    updatePhaseTaskPerformanceError( action.phaseIdPath, error ),
                    eventNotificationError( error, store$, ProjectStagePhaseTaskModule.identifier, "updatePhaseTaskPerformance" )
                ) )
            );
        })
    );
}


export interface UpdateMultipleTaskPerformancesAction extends ReduxAction<typeof UPDATE_MULTIPLE_TASK_PERFORMANCES> {
    projectId: Identifier<number>
    taskPerformances: ProjectPerformanceChangeType[]
}
export interface UpdateMultipleTaskPerformancesSuccessAction extends ReduxAction<typeof UPDATE_MULTIPLE_TASK_PERFORMANCES_SUCCESS> {}
export interface UpdateMultipleTaskPerformancesErrorAction extends ErrorAction< typeof UPDATE_MULTIPLE_TASK_PERFORMANCES_ERROR> {}

export const UPDATE_MULTIPLE_TASK_PERFORMANCES = "UPDATE_MULTIPLE_TASK_PERFORMANCES"
export const UPDATE_MULTIPLE_TASK_PERFORMANCES_SUCCESS = "UPDATE_MULTIPLE_TASK_PERFORMANCES_SUCCESS"
export const UPDATE_MULTIPLE_TASK_PERFORMANCES_ERROR = "UPDATE_MULTIPLE_TASK_PERFORMANCES_ERROR"

export function updateMultipleTaskPerformances(
    taskPerformances: ProjectPerformanceChangeType[],
    projectId: Identifier<number>
): UpdateMultipleTaskPerformancesAction {
    return { type: UPDATE_MULTIPLE_TASK_PERFORMANCES, taskPerformances, projectId };
}
export function updateMultipleTaskPerformancesSuccess(): UpdateMultipleTaskPerformancesSuccessAction {
    return { type: UPDATE_MULTIPLE_TASK_PERFORMANCES_SUCCESS };
}
export function updateMultipleTaskPerformancesError( error: any ): UpdateMultipleTaskPerformancesErrorAction {
    return { type: UPDATE_MULTIPLE_TASK_PERFORMANCES_ERROR, error };
}

export function updateMultipleTaskPerformancesEpic(
    action$: Observable< UpdateMultipleTaskPerformancesAction >,
    store$: StateObservable< AppState >
) {

    return action$.pipe(
        ofType( UPDATE_MULTIPLE_TASK_PERFORMANCES ),
        switchMap( action => {

            const url = `${ APP_CONFIG.baseWeb }projects/${ action.projectId.rawValue }/multiple-performances`;

            return ajax( APP_CONFIG.getRequestObject(
                url,
                RouteMethods.post,
                action.taskPerformances
            ) ).pipe(
                mergeMap( _ => of(
                    updateMultipleTaskPerformancesSuccess(),
                    fetchProjectById( action.projectId ),
                    fetchProjectAppendix( action.projectId, getInitialViewSettings( ProjectAppendixModule.identifier ) ),
                    eventNotificationSuccess()
                )),
                catchError( error => of(
                    updateMultipleTaskPerformancesError( error ),
                    eventNotificationError( error, store$, ProjectStagePhaseTaskModule.identifier, "updateMultipleTaskPerformances" )
                ) )
            );
        })
    );

}


/**
 * FETCH NEXT ID
 */
export interface FetchProjectPhaseTaskNextIdAction extends ReduxAction< typeof PROJECT_PHASE_TASK_NEXT_ID> {

}

export interface FetchProjectPhaseTaskNextIdSuccessAction extends ReduxAction<typeof PROJECT_PHASE_TASK_NEXT_ID_SUCCESS > {
    payload: number
}
export interface FetchProjectPhaseTaskNextIdErrorAction extends ErrorAction<typeof PROJECT_PHASE_TASK_NEXT_ID_ERROR> {

}

export type FetchProjectPhaseTaskNextIdActionTypes = FetchProjectPhaseTaskNextIdAction
    | FetchProjectPhaseTaskNextIdSuccessAction
    | FetchProjectPhaseTaskNextIdErrorAction

export const PROJECT_PHASE_TASK_NEXT_ID = "PROJECT_PHASE_TASK_NEXT_ID";
export const PROJECT_PHASE_TASK_NEXT_ID_SUCCESS = "PROJECT_PHASE_TASK_NEXT_ID_SUCCESS";
export const PROJECT_PHASE_TASK_NEXT_ID_ERROR = "PROJECT_PHASE_TASK_NEXT_ID_ERROR";

export function fetchProjectPhaseTaskNextId(): FetchProjectPhaseTaskNextIdAction {
    return { type: PROJECT_PHASE_TASK_NEXT_ID };
}
export function fetchProjectPhaseTaskNextIdSuccess( payload: number ): FetchProjectPhaseTaskNextIdSuccessAction {
    return { type: PROJECT_PHASE_TASK_NEXT_ID_SUCCESS, payload };
}
export function fetchProjectPhaseTaskNextIdError( error: any ): FetchProjectPhaseTaskNextIdErrorAction {
    return { type: PROJECT_PHASE_TASK_NEXT_ID_ERROR, error };
}

export function fetchProjectPhaseTaskNextIdEpic(
    action$: Observable<FetchProjectPhaseTaskNextIdAction>,
    store$: StateObservable<AppState>
) {
    return action$.pipe(
        ofType( PROJECT_PHASE_TASK_NEXT_ID ),
        switchMap( action => {
            const path = APP_CONFIG.baseWeb + 'projects/phases/tasks/next-id';

            return ajax(
                APP_CONFIG.getRequestObject( path )
            ).pipe(
                map( response => fetchProjectPhaseTaskNextIdSuccess( response.response as number )),
                catchError( error => concat(
                        of( fetchProjectPhaseTaskNextIdError( error ) ),
                        of( eventNotificationError( error, store$, "project_phases", "nextId" ) ),
                        asyncScheduler
                    )
                )
            )
        } )
    );
}